import { isDefined } from "@clipboard-health/util-ts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { isOfferFreshForShift } from "./isOfferValidForShift";
import { getOfferCacheKey, useCachedOffer, useCreateOffer } from "./useCreateOffer";

interface AutoRefreshOfferOptions {
  enabled?: boolean;
}

/**
 * Automatically refreshes an offer for a shift if it has expired.
 *
 * This is useful for shifts that are displayed in a list, where the offer may expire
 * after the shift is displayed.
 */
export function useAutoRefreshOffer(
  params: { shiftId?: string },
  options: AutoRefreshOfferOptions = {}
) {
  const { shiftId } = params;
  const { enabled = true } = options;

  const queryClient = useQueryClient();

  const { mutate: createOffer, isError, isLoading } = useCreateOffer(params);
  const offer = useCachedOffer(shiftId);

  const shouldCreateOffer =
    isDefined(shiftId) && enabled && !isOfferFreshForShift({ shiftId, offer });

  useEffect(() => {
    if (isError) {
      // Can't create an offer, so don't try again
      return;
    }

    if (isDefined(shiftId) && shouldCreateOffer) {
      const cacheKey = getOfferCacheKey(shiftId);
      const hasPendingMutation = queryClient.getMutationCache().find({
        exact: false,
        fetching: true,
        mutationKey: cacheKey,
      });

      if (!hasPendingMutation) {
        createOffer({ shiftId });
      }
    }
  }, [shiftId, shouldCreateOffer, queryClient, createOffer, isError]);

  return {
    data: { offer },
    isLoading,
    isError,
  };
}
